import type { TranslateFn } from "../../i18n";
import type { Profile } from "../types";
import { getUnitLabel } from "../utils/units";

/**
 * Confirmation modal shown before the "Find My Best Fit" fast path fires a
 * recommendation against the user's stored profile. The user sees exactly
 * what measurements we're about to send (and in which unit system) so they
 * can catch an imperial vs metric mismatch before the API call.
 *
 * - Proceed → parent runs `recommendForProduct`
 * - Edit / close-X → parent returns to the basics step with the profile
 *   values intact, ready to be edited and resubmitted.
 *
 * Reuses the existing `.ps-confirm-overlay` / `.ps-confirm-modal` CSS already
 * shipped for `DeleteConfirmModal` in `MySizingProfilesView` — same visual
 * language, no new CSS class to maintain.
 */
export function ConfirmMeasurementsModal({
  profile,
  onProceed,
  onEdit,
  t,
}: {
  profile: Profile;
  onProceed: () => void;
  onEdit: () => void;
  t: TranslateFn;
}) {
  // Prefer the heightUnit/weightUnit that the user picked when creating this
  // profile. Fall back to "cm"/"kg" so the label stays sensible for legacy
  // profiles missing those fields.
  const heightUnit = profile.heightUnit === "in" || profile.heightUnit === "ft" ? "in" : "cm";
  const weightUnit = profile.weightUnit === "lbs" ? "lbs" : "kg";
  const systemLabel = getUnitLabel(heightUnit); // "Metric" | "Imperial"

  const formatHeight = (h: number | undefined): string => {
    if (!h) return "—";
    if (heightUnit === "in") {
      const ft = Math.floor(h / 12);
      const inches = Math.round(h % 12);
      return `${ft}'${inches}"`;
    }
    return `${Math.round(h)} cm`;
  };
  const formatWeight = (w: number | undefined): string => {
    if (!w) return "—";
    return `${Math.round(w)} ${weightUnit}`;
  };

  const height = profile.height ?? profile.heightCm;
  const weight = profile.weight ?? profile.weightKg;

  return (
    <div className="ps-confirm-overlay" onClick={onEdit}>
      <div className="ps-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          aria-label={t("Close")}
          onClick={onEdit}
          style={{
            position: "absolute", top: "0.75vw", right: "0.75vw",
            width: "1.8vw", height: "1.8vw", borderRadius: "50%",
            background: "transparent", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--ps-text-muted)",
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <p style={{ fontWeight: 700, marginBottom: "0.4vw" }}>
          {t("Confirm your measurements")}
        </p>
        <small style={{ color: "var(--ps-text-muted)" }}>
          {systemLabel
            ? t("You chose") + " " + systemLabel + ". " + t("Review before continuing.")
            : t("Review before continuing.")}
        </small>

        <ul style={{
          listStyle: "none", padding: 0, margin: "0.8vw 0 0.2vw 0",
          width: "100%", textAlign: "left",
          fontSize: "0.78vw", lineHeight: 1.6,
        }}>
          <li style={{ display: "flex", justifyContent: "space-between", gap: "1vw" }}>
            <span style={{ color: "var(--ps-text-muted)" }}>{t("Height")}</span>
            <span style={{ fontWeight: 600 }}>{formatHeight(height)}</span>
          </li>
          <li style={{ display: "flex", justifyContent: "space-between", gap: "1vw" }}>
            <span style={{ color: "var(--ps-text-muted)" }}>{t("Weight")}</span>
            <span style={{ fontWeight: 600 }}>{formatWeight(weight)}</span>
          </li>
          {profile.age ? (
            <li style={{ display: "flex", justifyContent: "space-between", gap: "1vw" }}>
              <span style={{ color: "var(--ps-text-muted)" }}>{t("Age")}</span>
              <span style={{ fontWeight: 600 }}>{profile.age}</span>
            </li>
          ) : null}
          <li style={{ display: "flex", justifyContent: "space-between", gap: "1vw" }}>
            <span style={{ color: "var(--ps-text-muted)" }}>{t("Gender")}</span>
            <span style={{ fontWeight: 600 }}>
              {profile.gender === "female" ? t("Female") : t("Male")}
            </span>
          </li>
        </ul>

        <div className="ps-confirm-actions">
          <button type="button" className="ps-confirm-cancel" onClick={onEdit}>
            {t("Edit")}
          </button>
          <button type="button" className="ps-confirm-delete" onClick={onProceed}>
            {t("Proceed")}
          </button>
        </div>
      </div>
    </div>
  );
}
